import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  QUICK_ACTION_SOURCES,
  loadQuickActionPrescriptions,
  sendQuickActionPrescription,
} from './patientQuickActionsService'

// "Visit no. 3 · 31/01/2026, 3:00 pm IST" — event time rendered in IST (clinics are India-based).
function formatVisitLabel(item) {
  let dateStr = 'date unavailable'
  if (item?.eventStartTime) {
    try {
      dateStr = `${new Date(item.eventStartTime).toLocaleString('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })} IST`
    } catch (e) {
      dateStr = new Date(item.eventStartTime).toISOString()
    }
  }
  const visit = item?.visitNumber ? `Visit no. ${item.visitNumber}` : 'Prescription'
  return `${visit} · ${dateStr}`
}

// Confirm-and-send dialog for the "Send Prescription" quick action. On the session page the Rx is
// unambiguous (the event's Rx). In a patient drawer (no bookedEventId) we load the patient's
// prescriptions and let the user pick which visit's Rx to send. Delivery channel/mode follow the
// clinic-brand config, so there is no channel choice here.
export default function SendPrescriptionDialog({ open, onClose, contactContext, patientName, onSent }) {
  // null when idle, otherwise the delivery mode currently sending ('attachment' | 'link') so we can show
  // the spinner on the button that was clicked while disabling both.
  const [sendingMode, setSendingMode] = useState(null)
  const sending = !!sendingMode
  const [prescriptions, setPrescriptions] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [selectedBookedEventId, setSelectedBookedEventId] = useState('')

  const resolvedName = patientName || 'the patient'
  const isSupport = contactContext?.source === QUICK_ACTION_SOURCES.SUPPORT_PATIENT
  // Selection is needed when we don't already have a specific booked event (i.e. the drawer).
  const needsSelection = !contactContext?.bookedEventId && !isSupport

  useEffect(() => {
    if (!open) {
      return undefined
    }
    setSelectedBookedEventId('')
    setPrescriptions([])
    if (!needsSelection) {
      return undefined
    }
    let active = true
    setLoadingList(true)
    loadQuickActionPrescriptions(contactContext).then((result) => {
      if (!active) {
        return
      }
      const list = (result?.prescriptions || []).filter((item) => item?.bookedEventId)
      setPrescriptions(list)
      setSelectedBookedEventId(list[0]?.bookedEventId || '')
      setLoadingList(false)
    })
    return () => {
      active = false
    }
  }, [open, needsSelection, contactContext])

  const noneAvailable = needsSelection && !loadingList && prescriptions.length === 0
  const canSend =
    !sending && !loadingList && !noneAvailable && (!needsSelection || !!selectedBookedEventId)

  const handleSend = useCallback(
    async (deliveryMode) => {
      const sendContext = needsSelection
        ? { ...contactContext, bookedEventId: selectedBookedEventId }
        : contactContext
      setSendingMode(deliveryMode)
      const result = await sendQuickActionPrescription(sendContext, deliveryMode)
      setSendingMode(null)
      if (result?.ok) {
        const label = deliveryMode === 'attachment' ? 'a PDF attachment' : 'a secure link'
        toast.success(`Prescription sent to ${resolvedName} as ${label}.`)
        onSent?.()
        onClose?.()
      } else {
        toast.error(result?.message || 'Unable to send the prescription.')
      }
    },
    [needsSelection, contactContext, selectedBookedEventId, resolvedName, onClose, onSent],
  )

  return (
    <Dialog open={!!open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <MedicationOutlinedIcon sx={{ color: '#4338ca' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Send prescription</Typography>
        <IconButton
          onClick={onClose}
          disabled={sending}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ mb: 1 }}>
          Send {needsSelection ? 'a prescription' : 'the prescription'} to{' '}
          <strong>{resolvedName}</strong> over WhatsApp and email.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Choose how to deliver it — as a PDF <strong>attachment</strong> or a secure{' '}
          <strong>link</strong>.
        </Typography>

        {needsSelection && (
          <Box sx={{ mt: 2 }}>
            {loadingList ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Loading prescriptions…
                </Typography>
              </Box>
            ) : noneAvailable ? (
              <Alert severity="info">No prescriptions were found for this patient.</Alert>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel id="rx-select-label">Prescription to send</InputLabel>
                <Select
                  labelId="rx-select-label"
                  label="Prescription to send"
                  value={selectedBookedEventId}
                  onChange={(event) => setSelectedBookedEventId(event.target.value)}
                >
                  {prescriptions.map((item) => (
                    <MenuItem key={item.bookedEventId} value={item.bookedEventId}>
                      {formatVisitLabel(item)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none', mr: 'auto' }}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSend('link')}
          disabled={!canSend}
          startIcon={
            sendingMode === 'link' ? <CircularProgress size={16} color="inherit" /> : <LinkRoundedIcon />
          }
          sx={{
            textTransform: 'none',
            borderColor: 'rgba(67,56,202,0.5)',
            color: '#4338ca',
            '&:hover': { borderColor: '#3730a3', backgroundColor: 'rgba(67,56,202,0.06)' },
          }}
        >
          {sendingMode === 'link' ? 'Sending…' : 'Send as link'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSend('attachment')}
          disabled={!canSend}
          startIcon={
            sendingMode === 'attachment' ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AttachFileRoundedIcon />
            )
          }
          sx={{ textTransform: 'none', bgcolor: '#4338ca', '&:hover': { bgcolor: '#3730a3' } }}
        >
          {sendingMode === 'attachment' ? 'Sending…' : 'Send as attachment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
